import styled from '@emotion/styled';
import { Formik } from 'formik';
import { mapValues } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { MdOpenInNew } from 'react-icons/md';
import { generatePath, Link, useParams } from 'react-router-dom';

import { assessmentManagerHomeRoute, assessmentPreviewRoute } from 'App/common/routeLookup';
import ClickLink from 'App/components/styles/ClickLink';
import PageTitle from 'App/components/styles/PageTitle/PageTitle';
import ToggleLabel from 'App/components/ToggleSwitch/ToggleLabel';
import { saveQuestions } from 'App/services/AssessmentQuestionService';
import AssessmentService from 'App/services/AssessmentService';
import { useUserPreferences } from 'App/contexts/UserPreferencesContext';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import { queryClient } from 'Shared/queryClient';
import toast from 'Shared/toast';
import assessmentSettingsPropType from '../types/assessmentSettings';
import AssessmentManagerScoringModal from './AssessmentManagerScoringModal';
import AssessmentManagerQuestionBreadcrumbs from './components/AssessmentManagerQuestionBreadcrumbs';
import AssessmentManagerQuestionsArea from './components/AssessmentManagerQuestionsArea';
import AssessmentManagerQuestionsToolbar from './components/AssessmentManagerQuestionsToolbar';
import assessmentRiskPropType from './types/assessmentRiskPropType';
import questionLinkPropType from './types/questionLinkPropType';
import questionPropType from './types/questionPropType';
import convertAssessmentQuestionToView from './utils/convertAssessmentQuestionToView';
import convertAssessmentQuestionViewToSave from './utils/convertAssessmentQuestionViewToSave';
import convertQuestionLinkToView from './utils/convertQuestionLinkToView';
import convertQuestionLinkViewToSave from './utils/convertQuestionLinkViewToSave';
import flattenToDotNotation from './utils/flattenToDotNotation';
import getExpandedStateFromSession from './utils/getExpandedStateFromSession';

const ScoringEnabledRow = styled.div`
    display: flex;
    align-items: flex-end;
    margin-top: 15px;

    & ${ToggleLabel} {
        margin-top: 8px;
        margin-bottom: 6px;
    }

    & .btn {
        margin-left: 7px;
    }

    & .btn-link {
        margin-left: 1.2em;
    }
`;

const ExistingScoringRow = styled.div`
    margin-left: 46px;
`;

const expandedVals = {
    main: true,
    sections: {
        attributes: true,
        text: true,
        answers: true,
    },
};
const collapsedVals = {
    main: false,
    sections: {
        attributes: false,
        text: false,
        answers: false,
    },
};

const AssessmentManagerQuestionsForm = ({ assessment, assessmentRiskOptions, assessmentQuestions, questionLinks }) => {
    const { categoryId, assessmentId } = useParams();
    const [scoringModalOpen, setScoringModalOpen] = useState(false);
    const [riskToEdit, setRiskToEdit] = useState(null);
    const { setPreference } = useUserPreferences();

    async function deleteUnusedScorings(values) {
        if (values.scoringEnabled) {
            return;
        }
        queryClient.invalidateQueries(['assessmentId', assessmentId, 'riskOptions']);
        return Promise.all(
            assessmentRiskOptions
                .filter((scoring) => !!scoring.company_id)
                .map((scoring) => AssessmentService.deleteRisk(assessmentId, scoring.id))
        );
    }

    async function saveAssessment(values) {
        try {
            const questionsPayload = Object.fromEntries(
                Object.entries(values.questions).map(([id, question]) => [
                    id,
                    {
                        ...convertAssessmentQuestionViewToSave(question, values.scoringEnabled),
                        link: convertQuestionLinkViewToSave(
                            values.questionLinks[question.official_question_id || question.question_id]
                        ),
                    },
                ])
            );
            await saveQuestions(assessmentId, questionsPayload);
            queryClient.invalidateQueries(['assessmentId', assessmentId, 'questions']);
            toast.success('Assessment updated successfully.');
        } catch (err) {
            const errorMessage =
                err.response?.status === 403 && err.headers['content-type'] === 'text/html' ? (
                    <div>
                        You’ve encountered a security block. Please see{' '}
                        <a
                            href="https://tkn-cdn.therigy.com/therigystm/gettingstarted/resources/TherigySTM_Resource_403_Errors.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            403 Resource Document
                        </a>{' '}
                        for troubleshooting.
                    </div>
                ) : (
                    err.response?.data?.exception[0]?.message || err.data?.message
                );
            toast.error(errorMessage);
            throw err;
        }
    }

    return (
        <Formik
            initialValues={{
                scoringEnabled: assessmentRiskOptions.length > 0,
                questions: mapValues(assessmentQuestions, convertAssessmentQuestionToView),
                questionLinks: mapValues(questionLinks, convertQuestionLinkToView),
                questionsExpanded: mapValues(assessmentQuestions, (q) =>
                    getExpandedStateFromSession(`assessmentManagerQuestionExpanded.${q.question_id}`, expandedVals)
                ),
            }}
            onSubmit={async (values) => {
                await deleteUnusedScorings(values);
                return saveAssessment(values);
            }}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                    <PageTitle>{assessment.name}</PageTitle>
                    <div className="container">
                        <AssessmentManagerQuestionBreadcrumbs assessment={assessment} />

                        <div>
                            <Link to={generatePath(assessmentManagerHomeRoute, { categoryId })}>
                                Back To Therapeutic Category
                            </Link>
                            {' | '}
                            <Link to={generatePath(assessmentPreviewRoute, { assessmentId })} target="_blank">
                                Assessment Preview <MdOpenInNew />
                            </Link>
                            {' | '}
                            <Link
                                to={{
                                    pathname: generatePath(assessmentPreviewRoute, { assessmentId }),
                                    search: 'expanded=true',
                                }}
                                target="_blank"
                            >
                                Assessment Preview (expanded) <MdOpenInNew />
                            </Link>
                        </div>

                        <AssessmentManagerQuestionsToolbar />

                        <ScoringEnabledRow>
                            <ToggleSwitchField
                                fieldName="scoringEnabled"
                                label="Question Scoring Enabled"
                                disabled={assessmentRiskOptions.some((scoring) => !scoring.company_id)}
                            />
                            {values.scoringEnabled && (
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        setRiskToEdit(null);
                                        setScoringModalOpen(true);
                                    }}
                                >
                                    <FaPlus /> Create Scoring
                                </Button>
                            )}

                            <div style={{ flexGrow: 100 }} />

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                    setFieldValue(
                                        'questionsExpanded',
                                        mapValues(values.questions, (q) => {
                                            Object.entries(flattenToDotNotation(collapsedVals)).map(([key, value]) => {
                                                return setPreference(
                                                    `assessmentManagerQuestionExpanded.${q.question_id}.${key}`,
                                                    value
                                                );
                                            });
                                            return collapsedVals;
                                        })
                                    );
                                }}
                            >
                                Collapse All
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                    setFieldValue(
                                        'questionsExpanded',
                                        mapValues(values.questions, (q) => {
                                            Object.entries(flattenToDotNotation(expandedVals)).map(([key, value]) => {
                                                return setPreference(
                                                    `assessmentManagerQuestionExpanded.${q.question_id}.${key}`,
                                                    value
                                                );
                                            });
                                            return expandedVals;
                                        })
                                    );
                                }}
                            >
                                Expand All
                            </Button>
                        </ScoringEnabledRow>
                        {values.scoringEnabled && (
                            <ExistingScoringRow>
                                {assessmentRiskOptions.map((scoring, index) => (
                                    <React.Fragment key={scoring.id}>
                                        {index > 0 && ' | '}
                                        <ClickLink
                                            onClick={() => {
                                                setRiskToEdit(scoring);
                                                setScoringModalOpen(true);
                                            }}
                                        >
                                            {scoring.risk_name}
                                        </ClickLink>
                                    </React.Fragment>
                                ))}
                            </ExistingScoringRow>
                        )}

                        {scoringModalOpen && (
                            <AssessmentManagerScoringModal
                                risk={riskToEdit}
                                onClose={() => setScoringModalOpen(false)}
                            />
                        )}

                        <AssessmentManagerQuestionsArea assessment={assessment} />
                    </div>
                </form>
            )}
        </Formik>
    );
};

AssessmentManagerQuestionsForm.propTypes = {
    assessment: assessmentSettingsPropType.isRequired,
    assessmentRiskOptions: PropTypes.arrayOf(assessmentRiskPropType).isRequired,
    assessmentQuestions: PropTypes.objectOf(questionPropType).isRequired,
    questionLinks: PropTypes.oneOfType([
        PropTypes.objectOf(questionLinkPropType),
        PropTypes.arrayOf(questionLinkPropType),
    ]).isRequired,
};

export default AssessmentManagerQuestionsForm;
